import React, { useState } from 'react';
import AcSwitch from './AcSwitch';
import ACSchedule from './ACSchedule';
import {
  Power,
  Snowflake,
  ThermometerSun,
  Fan,
  Gauge,
  Wind,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
} from 'lucide-react';

function AcControlling() {
  const [activeUnits, setActiveUnits] = useState(['AC Unite 1']);
  const [mode, setMode] = useState('auto');
  const [power, setPower] = useState(false);
  const [temperature, setTemperature] = useState(24);
  const [fanSpeed, setFanSpeed] = useState(2);
  const [swingMode, setSwingMode] = useState(false);

  const acUnits = ['AC Unit - 1', 'AC Unit - 2', 'AC Unit - 3', 'AC Unit - 4'];
  const fanSpeeds = ['Low', 'Medium', 'High', 'Turbo'];
  const modes = ['Cool', 'Heat', 'Dry', 'Fan'];
  const [activeMode, setActiveMode] = useState(modes[0]);

  const handleACToggle = (ac) => {
    setActiveUnits(prev =>
      prev.includes(ac)
        ? prev.filter(unit => unit !== ac)
        : [...prev, ac]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-teal-800 to-emerald-900">
      <div className="container mx-auto px-4 py-8 sm:py-12">
        {/* AC Unit Selector */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-xl p-2 w-full max-w-4xl border border-white/20 mx-auto">
          <AcSwitch
            acUnits={acUnits}
            onToggle={handleACToggle}
            activeUnits={activeUnits}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 w-full">
          
          {/* Schedule Panel */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-xl p-6 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <svg className="w-6 h-6 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              AC Schedule
            </h2>
            <ACSchedule />
          </div>

          {/* Control Panel */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-xl p-6 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <svg className="w-6 h-6 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              AC Control Panel
            </h2>

            {/* Power Button */}
            <div className="flex justify-center mb-8">
              <button
                onClick={() => setPower(!power)}
                className={`p-5 rounded-full transition-all duration-300 shadow-lg ${
                  power 
                    ? 'bg-gradient-to-br from-green-400 to-green-600 text-white ring-4 ring-green-400/30' 
                    : 'bg-white/20 text-white/70 hover:bg-white/30'
                }`}
              >
                <Power size={32} strokeWidth={power ? 2.5 : 2} />
              </button>
            </div>

            {/* Mode Toggle */}
            <div className="flex gap-3 mb-8">
              {['auto', 'manual'].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-3 rounded-xl text-lg font-medium transition-all duration-300 ${
                    mode === m 
                      ? 'bg-gradient-to-r from-blue-400 to-teal-500 text-white shadow-md' 
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                  }`}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>

            {power && (
              <>
                {/* Temperature Control */}
                <div className="mb-8 text-center">
                  <div className="text-7xl font-bold text-white mb-4 relative">
                    {temperature}
                    <span className="absolute top-0 text-3xl text-blue-200">°C</span>
                  </div>
                  <div className="flex justify-center gap-4 sm:gap-6">
                    <button
                      onClick={() => setTemperature((t) => Math.min(30, t + 1))}
                      className="p-3 sm:p-4 rounded-full bg-gradient-to-br from-blue-400 to-teal-500 text-white hover:from-blue-500 hover:to-teal-600 transition-all shadow-lg"
                    >
                      <ArrowUpCircle size={28} className="sm:size-8" />
                    </button>
                    <button
                      onClick={() => setTemperature((t) => Math.max(16, t - 1))}
                      className="p-3 sm:p-4 rounded-full bg-gradient-to-br from-blue-400 to-teal-500 text-white hover:from-blue-500 hover:to-teal-600 transition-all shadow-lg"
                    >
                      <ArrowDownCircle size={28} className="sm:size-8" />
                    </button>
                  </div>
                </div>

                {mode === 'manual' && (
                  <>
                    {/* Mode Selection */}
                    <div className="grid grid-cols-2 gap-3 mb-8">
                      {modes.map((m) => (
                        <button
                          key={m}
                          onClick={() => setActiveMode(m)}
                          className={`p-3 sm:p-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all duration-300 ${
                            activeMode === m 
                              ? 'bg-gradient-to-r from-blue-400 to-teal-500 text-white shadow-md' 
                              : 'bg-white/10 text-white/80 hover:bg-white/20'
                          }`}
                        >
                          {m === 'Cool' && <Snowflake size={22} className="sm:size-6" />}
                          {m === 'Heat' && <ThermometerSun size={22} className="sm:size-6" />}
                          {m === 'Dry' && <Wind size={22} className="sm:size-6" />}
                          {m === 'Fan' && <Fan size={22} className="sm:size-6" />}
                          <span className="text-sm sm:text-base font-medium">{m}</span>
                        </button>
                      ))}
                    </div>

                    {/* Fan Speed */}
                    <div className="mb-8">
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <Gauge size={22} className="text-blue-200 sm:size-6" />
                        <span className="text-white/90 font-medium text-lg">Fan Speed</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 sm:gap-3">
                        {fanSpeeds.map((speed, index) => (
                          <button
                            key={speed}
                            onClick={() => setFanSpeed(index)}
                            className={`py-2 px-1 sm:py-3 sm:px-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                              fanSpeed === index 
                                ? 'bg-gradient-to-r from-blue-400 to-teal-500 text-white shadow' 
                                : 'bg-white/10 text-white/80 hover:bg-white/20'
                            }`}
                          >
                            {speed}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Swing Mode */}
                    <button
                      onClick={() => setSwingMode(!swingMode)}
                      className={`w-full py-3 sm:py-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 ${
                        swingMode 
                          ? 'bg-gradient-to-r from-blue-400 to-teal-500 text-white shadow-md' 
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                    >
                      <RefreshCw size={20} className={`sm:size-6 ${swingMode ? 'animate-spin' : ''}`} />
                      <span className="font-medium text-sm sm:text-base">Swing Mode {swingMode ? 'On' : 'Off'}</span>
                    </button>
                  </>
                )}
              </>
            )}

            {/* Status Message */}
            {!power && (
              <div className="text-center text-white/70 mt-6 text-lg italic">
                Press power button to activate controls
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcControlling;